import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";

import { fetchAiBudget } from "./aiBudgetSlice";
import { sendPronunciationUpload } from "../../services/pronunciationUploadService";
import { sharedApi, getAuthToken } from "../../services/sharedApi";
import { toApiError } from "../../utils/errorUtils";

import type { RootState } from "..";
import type { ApiError } from "../../utils/errorUtils";
import type {
  PronunciationConfiguration,
  PronunciationText,
  PronunciationAssessment,
  PronunciationHistory,
  PronunciationProgress,
  PronunciationAttempt,
} from "@lingoshade/domain";

interface PronunciationState {
  configuration: PronunciationConfiguration | null;
  configLoading: boolean;
  pendingRequests: string[];
  texts: Record<string, PronunciationText>;
  results: Record<string, Record<string, PronunciationAssessment>>;
  error: ApiError | null;
  histories: Record<string, PronunciationHistory>;
  progress: Record<string, PronunciationProgress>;
  attempts: Record<string, PronunciationAttempt>;
  historyError: ApiError | null;
  historyLoading: boolean;
}
const initialState: PronunciationState = {
  configuration: null,
  configLoading: false,
  pendingRequests: [],
  texts: {},
  results: {},
  error: null,
  histories: {},
  progress: {},
  attempts: {},
  historyError: null,
  historyLoading: false,
};

export const fetchPronunciationConfiguration = createAsyncThunk<
  PronunciationConfiguration,
  void,
  { state: RootState; rejectValue: ApiError }
>(
  "pronunciation/config",
  async (
    _,
    { rejectWithValue },
  ): Promise<
    PronunciationConfiguration | ReturnType<typeof rejectWithValue>
  > => {
    try {
      return await sharedApi.pronunciation.getConfiguration(getAuthToken());
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error));
    }
  },
  {
    condition: (_, { getState }): boolean =>
      !getState().pronunciation.configLoading &&
      !getState().pronunciation.configuration,
  },
);

export const fetchPronunciationText = createAsyncThunk<
  PronunciationText,
  string,
  { rejectValue: ApiError }
>(
  "pronunciation/text",
  async (
    textId,
    { rejectWithValue },
  ): Promise<PronunciationText | ReturnType<typeof rejectWithValue>> => {
    try {
      return await sharedApi.pronunciation.getText(textId, getAuthToken());
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const fetchPronunciationHistory = createAsyncThunk<
  PronunciationHistory,
  string | { textId: string; cursor: string },
  { rejectValue: ApiError }
>(
  "pronunciation/history",
  async (
    input,
    { rejectWithValue },
  ): Promise<PronunciationHistory | ReturnType<typeof rejectWithValue>> => {
    try {
      return await sharedApi.pronunciation.getHistory(
        typeof input === "string" ? { textId: input } : input,
        getAuthToken(),
      );
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error));
    }
  },
);
export interface ProgressQuery {
  textId: string;
  start?: number;
  end?: number;
}
export function pronunciationProgressKey(query: ProgressQuery): string {
  return `${query.textId}:${query.start ?? "all"}:${query.end ?? "all"}`;
}
export const fetchPronunciationProgress = createAsyncThunk<
  PronunciationProgress,
  ProgressQuery,
  { rejectValue: ApiError }
>(
  "pronunciation/progress",
  async (
    query,
    { rejectWithValue },
  ): Promise<PronunciationProgress | ReturnType<typeof rejectWithValue>> => {
    try {
      return await sharedApi.pronunciation.getProgress(query, getAuthToken());
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error));
    }
  },
);
export const fetchPronunciationAttempt = createAsyncThunk<
  PronunciationAttempt,
  string,
  { rejectValue: ApiError }
>(
  "pronunciation/attempt",
  async (
    id,
    { rejectWithValue },
  ): Promise<PronunciationAttempt | ReturnType<typeof rejectWithValue>> => {
    try {
      return await sharedApi.pronunciation.getAttempt(id, getAuthToken());
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error));
    }
  },
);

export const submitPronunciation = createAsyncThunk<
  PronunciationAssessment,
  { uploadId: string; textId?: string },
  { state: RootState; rejectValue: ApiError }
>(
  "pronunciation/assess",
  async (
    { uploadId, textId },
    { getState, dispatch, rejectWithValue },
  ): Promise<PronunciationAssessment | ReturnType<typeof rejectWithValue>> => {
    try {
      return await sendPronunciationUpload(
        uploadId,
        getState().auth.user?.id ?? "",
        getAuthToken(),
        (getState().pronunciation.configuration?.settings.requestTimeoutMs ??
          30000) + 190_000,
      );
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error));
    } finally {
      if (textId) void dispatch(fetchPronunciationHistory(textId));
      void dispatch(
        fetchAiBudget(
          getState().aiBudget.status?.pronunciationLanguage || undefined,
        ),
      );
    }
  },
);

function saveResult(
  state: PronunciationState,
  result: PronunciationAssessment,
): void {
  if (!result.textId) return;
  for (const previous of Object.values(state.results[result.textId] ?? {})) {
    if (
      previous.referenceText !== result.referenceText ||
      previous.selection?.start !== result.selection?.start ||
      previous.selection?.end !== result.selection?.end
    )
      continue;
    if (
      previous.createdAt > result.createdAt ||
      (previous.createdAt === result.createdAt && previous.id > result.id)
    )
      return;
    delete state.results[result.textId][previous.id];
  }
  state.results[result.textId] ??= {};
  state.results[result.textId][result.id] = result;
}

const slice = createSlice({
  name: "pronunciation",
  initialState,
  reducers: { resetPronunciation: (): PronunciationState => initialState },
  extraReducers: (builder): void => {
    builder.addCase(fetchPronunciationConfiguration.pending, (state): void => {
      state.configLoading = true;
    });
    builder.addCase(
      fetchPronunciationConfiguration.fulfilled,
      (state, action): void => {
        if (!state.pendingRequests.includes(action.meta.requestId)) return;
        state.configLoading = false;
        state.configuration = action.payload;
      },
    );
    builder.addCase(
      fetchPronunciationConfiguration.rejected,
      (state, action): void => {
        if (!state.pendingRequests.includes(action.meta.requestId)) return;
        state.configLoading = false;
        state.error = action.payload ?? null;
      },
    );
    builder.addCase(fetchPronunciationText.fulfilled, (state, action): void => {
      if (!state.pendingRequests.includes(action.meta.requestId)) return;
      state.texts[action.payload.textId] = action.payload;
      state.error = null;
    });
    builder.addCase(fetchPronunciationText.rejected, (state, action): void => {
      if (!state.pendingRequests.includes(action.meta.requestId)) return;
      state.error = action.payload ?? null;
    });
    builder.addCase(
      fetchPronunciationHistory.fulfilled,
      (state, action): void => {
        if (!state.pendingRequests.includes(action.meta.requestId)) return;
        for (const result of action.payload.results) saveResult(state, result);
        const textId =
          typeof action.meta.arg === "string"
            ? action.meta.arg
            : action.meta.arg.textId;
        const previous = state.histories[textId];
        state.histories[textId] =
          typeof action.meta.arg === "string"
            ? { ...action.payload, results: [] }
            : {
                ...action.payload,
                attempts: [
                  ...(previous?.attempts ?? []),
                  ...(action.payload.attempts ?? []),
                ],
                results: [],
              };
        state.historyLoading = false;
      },
    );
    builder.addCase(submitPronunciation.fulfilled, (state, action): void => {
      if (!state.pendingRequests.includes(action.meta.requestId)) return;
      saveResult(state, action.payload);
      state.error = null;
    });
    builder.addCase(submitPronunciation.rejected, (state, action): void => {
      if (!state.pendingRequests.includes(action.meta.requestId)) return;
      state.error = action.payload ?? null;
    });
    builder.addCase(fetchPronunciationHistory.pending, (state): void => {
      state.historyLoading = true;
      state.historyError = null;
    });
    builder.addCase(
      fetchPronunciationHistory.rejected,
      (state, action): void => {
        if (!state.pendingRequests.includes(action.meta.requestId)) return;
        state.historyLoading = false;
        state.historyError = action.payload ?? null;
      },
    );
    builder.addCase(
      fetchPronunciationProgress.fulfilled,
      (state, action): void => {
        if (!state.pendingRequests.includes(action.meta.requestId)) return;
        state.progress[pronunciationProgressKey(action.meta.arg)] =
          action.payload;
      },
    );
    builder.addCase(
      fetchPronunciationAttempt.fulfilled,
      (state, action): void => {
        if (!state.pendingRequests.includes(action.meta.requestId)) return;
        state.attempts[action.payload.id] = action.payload;
      },
    );
    builder.addCase(fetchPronunciationProgress.pending, (state): void => {
      state.historyError = null;
    });
    builder.addCase(fetchPronunciationAttempt.pending, (state): void => {
      state.historyError = null;
    });
    builder.addMatcher(
      isAnyOf(
        fetchPronunciationProgress.rejected,
        fetchPronunciationAttempt.rejected,
      ),
      (state, action): void => {
        if (!state.pendingRequests.includes(action.meta.requestId)) return;
        state.historyError = action.payload ?? null;
      },
    );
    builder.addMatcher(
      isAnyOf(
        fetchPronunciationConfiguration.pending,
        fetchPronunciationText.pending,
        fetchPronunciationHistory.pending,
        submitPronunciation.pending,
        fetchPronunciationProgress.pending,
        fetchPronunciationAttempt.pending,
      ),
      (state, action): void => {
        state.pendingRequests.push(action.meta.requestId);
      },
    );
    builder.addMatcher(
      isAnyOf(
        fetchPronunciationConfiguration.fulfilled,
        fetchPronunciationConfiguration.rejected,
        fetchPronunciationText.fulfilled,
        fetchPronunciationText.rejected,
        fetchPronunciationHistory.fulfilled,
        fetchPronunciationHistory.rejected,
        submitPronunciation.fulfilled,
        submitPronunciation.rejected,
        fetchPronunciationProgress.fulfilled,
        fetchPronunciationProgress.rejected,
        fetchPronunciationAttempt.fulfilled,
        fetchPronunciationAttempt.rejected,
      ),
      (state, action): void => {
        state.pendingRequests = state.pendingRequests.filter(
          (id): boolean => id !== action.meta.requestId,
        );
      },
    );
  },
});
export const { resetPronunciation } = slice.actions;
export default slice.reducer;
